<template>
    <Layout :customer="customer" :menuItems="menuItems">
        <div class="max-w-7xl">
            <!-- Empty State -->
            <div v-if="totalPlans === 0" class="text-center py-12">
                <div class="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-4">
                    <svg class="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                </div>
                <h2 class="text-xl font-semibold text-gray-900 mb-2">Keine Fitness Abo Historie</h2>
                <p class="text-gray-600 mb-6">
                    Es wurden noch keine abgeschlossenen Fitness Abos gefunden.
                </p>
                <Link 
                    :href="`/customers/${customer.uuid}/fitness`"
                    class="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                    </svg>
                    Zurück zum aktuellen Plan
                </Link>
            </div>

            <!-- Content when there are plans -->
            <template v-else>
                <!-- Back Link -->
                <div class="pt-1">
                    <Link 
                        :href="`/customers/${customer.uuid}/fitness`"
                        class="inline-flex items-center text-gray-500 hover:text-gray-700 text-sm transition-colors"
                    >
                        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                        </svg>
                        Zurück zum aktuellen Plan
                    </Link>
                </div>
                
                <!-- Summary Section -->
                <div class="rounded-lg p-4 mb-6">
                    <h2 class="text-lg font-bold mb-2">Fitness Abo Verlauf</h2>
                    <p class="text-sm text-gray-600">
                        Anzeige: {{ currentPage }}/{{ totalPlans }} 
                        <span class="font-semibold">({{ totalPlans }} geschlossene Pläne insgesamt)</span>
                    </p>
                </div>

                <!-- Navigation and Plan Display -->
            <div class="flex items-center gap-4 px-4">
                <!-- Left Arrow -->
                <Link 
                    v-if="prevUrl"
                    :href="prevUrl"
                    class="p-3 rounded-full bg-gray-200 hover:bg-gray-300 transition-colors"
                    preserve-scroll
                >
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                    </svg>
                </Link>
                <div v-else class="p-3 rounded-full bg-gray-200 opacity-50 cursor-not-allowed">
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                    </svg>
                </div>

                <!-- Plan Card -->
                <div class="flex-1">
                    <Card v-if="currentPlan">
                        <template #title>
                            {{ currentPlan.unlimited_plan.name }}
                        </template>
                        <template #content>
                            <div class="mb-4">
                                <div class="mb-2">
                                    <span class="font-semibold">Zeitraum:</span> 
                                    {{ formatDate(currentPlan.date) }} - {{ formatDate(currentPlan.date_to) }}
                                </div>
                                <div class="mb-2">
                                    <span class="font-semibold">Geschlossen am:</span> 
                                    {{ formatDateTime(currentPlan.closed_at) }}
                                </div>
                                <div class="mb-2">
                                    <Tag :severity="getStatusSeverity(currentPlan.status)" 
                                         :value="getStatusLabel(currentPlan.status)"></Tag>
                                </div>
                                <div class="mb-2">
                                    <span class="font-semibold">Gesamtbuchungen:</span> 
                                    {{ currentPlan.bookingsCount }}
                                </div>
                            </div>

                            <!-- Devices Section -->
                            <div v-if="currentPlan.devices && currentPlan.devices.length > 0">
                                <div class="bg-[#EEEBE4] rounded-t-lg">
                                    <div class="px-4 py-2">
                                        <h3 class="text-sm font-bold">Geräte</h3>
                                    </div>
                                </div>
                                
                                <div class="bg-white rounded-b-lg border border-gray-200">
                                    <div class="px-4 py-2">
                                        <div v-for="device in currentPlan.devices" :key="device.name" 
                                             class="flex justify-between items-center py-2 border-b last:border-b-0">
                                            <span class="text-sm">{{ device.name }}</span>
                                            <span class="text-sm font-bold">{{ device.bookingsCount }} Buchungen</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </Card>
                </div>

                <!-- Right Arrow -->
                <Link 
                    v-if="nextUrl"
                    :href="nextUrl"
                    class="p-3 rounded-full bg-gray-200 hover:bg-gray-300 transition-colors"
                    preserve-scroll
                >
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                </Link>
                <div v-else class="p-3 rounded-full bg-gray-200 opacity-50 cursor-not-allowed">
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                </div>
            </div>

            <!-- Navigation Dots -->
            <div class="flex justify-center flex-wrap mt-6 gap-2 max-w-md mx-auto">
                <Link 
                    v-for="index in totalPlans" 
                    :key="index"
                    :href="`/customers/${customer.uuid}/fitness/history?page=${index}`"
                    class="w-2 h-2 rounded-full transition-colors"
                    :class="currentPage == index ? 'bg-blue-600' : 'bg-gray-300'"
                    preserve-scroll
                />
            </div>
            
            <!-- Spacer for bottom padding -->
            <div class="h-4"></div>
            </template>
        </div>
    </Layout>
</template>

<script setup>
import Layout from "../Layouts/Customer.vue";
import Card from "primevue/card";
import Tag from "primevue/tag";
import { Link } from "@inertiajs/inertia-vue3";
import moment from "moment";

const props = defineProps([
    'customer',
    'menuItems',
    'currentPlan',
    'currentPage',
    'totalPlans',
    'prevUrl',
    'nextUrl'
]);

// Formatting helpers
const formatDate = (date) => {
    return moment(date).format('DD.MM.YYYY');
};

const formatDateTime = (datetime) => {
    return moment(datetime).format('DD.MM.YYYY HH:mm');
};

const getStatusSeverity = (status) => {
    const statusMap = {
        'active': 'success',
        'expired': 'warning',
        'cancelled': 'danger'
    };
    return statusMap[status] || 'info';
};

const getStatusLabel = (status) => {
    const labelMap = {
        'active': 'Aktiv',
        'expired': 'Abgelaufen',
        'cancelled': 'Storniert'
    };
    return labelMap[status] || status;
};
</script>

<style scoped>
/* Add any custom styles if needed */
.transition-colors {
    transition: background-color 0.2s ease;
}
</style>